import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ImageAspectRatioIcon from '@mui/icons-material/ImageAspectRatio';
import PauseIcon from '@mui/icons-material/Pause';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import {
    Box,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Slider,
    Switch,
    ThemeProvider,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TrackedIconButton as IconButton } from '../../../components/TrackedComponents';
import { TIME_INTERVALS } from '../../../utils/TimeFormatter';
import palette from '../../ColorPalette';
import { RTLDirectionContext } from '../../Layout';

const PLAYBACK_SPEED_OPTIONS = [0.5, 1, 1.5, 2];

type VideoSettingsMenuOpen = null | 'settings' | 'playback_speed';

const formatTime = (time: number) => {
    if (isNaN(time) || time == Infinity) {
        return '00:00';
    }
    const days = Math.floor(time / TIME_INTERVALS.Day);
    const hours = Math.floor((time % TIME_INTERVALS.Day) / TIME_INTERVALS.Hour);
    const minutes = Math.floor((time % TIME_INTERVALS.Hour) / TIME_INTERVALS.Minute);
    const seconds = Math.floor(time % TIME_INTERVALS.Minute);
    if (days > 0) {
        return `${days}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`;
    } else if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export interface VideoControlsComponentProps {
    isPlaying: boolean;
    handleStart: () => void;
    handleStop: () => void;
    seekLocation: number;
    handleSeekerChange: (event: Event, newValue: number | number[]) => void;
    videoDuration: number;
    targetRef: React.RefObject<HTMLDivElement>;
    setShowDownloadModal: (show: boolean) => void;
    hideSecondary?: boolean;
    showOverlay?: boolean;
    setShowOverlay?: React.Dispatch<React.SetStateAction<boolean>>;
    fullScreen?: boolean;
    setFullScreen?: React.Dispatch<React.SetStateAction<boolean>>;
    playbackRate?: number;
    onPlaybackRateChange?: (rate: number) => void;
}

const VideoControlsComponent: React.FC<VideoControlsComponentProps> = (props: VideoControlsComponentProps) => {
    const [seekingInfo, setSeekingInfo] = useState<{ isDragging: boolean; wasPlaying: boolean }>({
        isDragging: false,
        wasPlaying: false,
    });
    const [menuOpen, setMenuOpen] = useState<VideoSettingsMenuOpen>(null);
    const settingsButtonRef = useRef<HTMLElement | null>(null);
    const { t } = useTranslation();
    const playbackRate = props.playbackRate ?? 1;
    const playbackRateLabel = `${playbackRate.toFixed(playbackRate % 1 === 0 ? 0 : 1)} x`;
    const isRTL = useContext(RTLDirectionContext);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sliderStyles: any = {
        color: palette.neutral[50],
        mx: 1,
        '& .MuiSlider-mark': {
            backgroundColor: palette.accent,
            opacity: 1,
            zIndex: 5,
        },
    };

    if (isRTL) {
        sliderStyles['& .MuiSlider-thumb'] = {
            transform: 'translateX(5px) translateY(-10px)',
        };
    } else {
        sliderStyles['& .MuiSlider-mark'].transform = 'scale(4) translateY(-15%) translateX(-15%)';
    }

    return (
        <ThemeProvider theme={(outerTheme) => ({ ...outerTheme, direction: 'ltr' })}>
            <Box
                sx={{
                    position: props.fullScreen ? 'absolute' : undefined,
                    bottom: props.fullScreen ? '0' : undefined,
                    width: '100%',
                    zIndex: 1001,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Tooltip title={t(`content.events.video.controls.${props.isPlaying ? 'pause' : 'play'}`)}>
                    <Box>
                        <IconButton
                            id="play-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                props.isPlaying ? props.handleStop() : props.handleStart();
                            }}
                            sx={{ color: palette.neutral[50] }}
                        >
                            {props.isPlaying ? (
                                <PauseIcon data-testid="PauseIcon" />
                            ) : (
                                <PlayArrowIcon data-testid="PlayArrowIcon" />
                            )}
                        </IconButton>
                    </Box>
                </Tooltip>
                <Slider
                    aria-label="seek"
                    value={props.seekLocation}
                    onChange={props.handleSeekerChange}
                    sx={sliderStyles}
                    onMouseDown={() => {
                        props.handleStop();
                        setSeekingInfo({ isDragging: true, wasPlaying: props.isPlaying });
                    }}
                    onMouseUp={() => {
                        if (seekingInfo.wasPlaying) {
                            props.handleStart();
                        }
                        setSeekingInfo((p) => ({ isDragging: false, wasPlaying: p.wasPlaying }));
                    }}
                />
                <Box sx={{ display: 'flex' }}>
                    <Typography sx={{ whiteSpace: 'nowrap', color: palette.neutral[50], fontSize: 14 }}>
                        {props.seekLocation && props.videoDuration
                            ? formatTime((props.seekLocation * props.videoDuration) / 100)
                            : '00:00'}
                    </Typography>
                    <Typography sx={{ whiteSpace: 'nowrap', color: palette.neutral[400], fontSize: 14 }}>/</Typography>
                    <Typography sx={{ whiteSpace: 'nowrap', color: palette.neutral[400], fontSize: 14 }}>
                        {props.videoDuration ? formatTime(props.videoDuration) : '00:00'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', px: 1 }}>
                    <Tooltip title={t('content.events.video.controls.settings')}>
                        <Box ref={settingsButtonRef}>
                            <IconButton
                                id="settings-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpen('settings');
                                }}
                                sx={{ color: palette.neutral[50], px: 0.25 }}
                            >
                                <SettingsIcon />
                            </IconButton>
                        </Box>
                    </Tooltip>
                    <Menu
                        id="video-settings-menu"
                        anchorEl={menuOpen === 'settings' ? settingsButtonRef.current : null}
                        open={menuOpen === 'settings'}
                        onClose={() => setMenuOpen(null)}
                        disablePortal={!!props.fullScreen}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        PaperProps={{
                            sx: {
                                backgroundColor: 'rgba(38, 38, 38, 0.8)',
                                color: palette.neutral[50],
                                minWidth: 200,
                                mt: -0.25,
                                '& .MuiListItemIcon-root': { color: palette.neutral[50], minWidth: 36 },
                                '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                            },
                        }}
                        MenuListProps={{ sx: { py: 0 } }}
                    >
                        <MenuItem
                            id="menu-download"
                            onClick={() => {
                                setMenuOpen(null);
                                props.setShowDownloadModal(true);
                            }}
                        >
                            <ListItemIcon>
                                <DownloadIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t('content.events.video.controls.download')} />
                        </MenuItem>
                        {props.hideSecondary && (
                            <MenuItem
                                id="menu-overlay-toggle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.setShowOverlay?.((prev) => !prev);
                                }}
                            >
                                <ListItemIcon>
                                    {props.showOverlay ? (
                                        <ImageAspectRatioIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                                    ) : (
                                        <PictureInPictureAltIcon
                                            fontSize="small"
                                            sx={{ transform: 'rotateX(180deg)' }}
                                        />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={t(`content.events.video.controls.show_overlay`)}
                                    sx={{ mx: 1 }}
                                />
                                <Switch
                                    size="small"
                                    checked={!!props.showOverlay}
                                    onChange={() => props.setShowOverlay?.((prev) => !prev)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        '& .MuiSwitch-switchBase': { color: palette.neutral[50] },
                                        '& .MuiSwitch-track': { backgroundColor: palette.neutral[50] },
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: palette.secondary },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: palette.secondary,
                                        },
                                    }}
                                />
                            </MenuItem>
                        )}
                        {props.onPlaybackRateChange != null && (
                            <MenuItem
                                id="menu-playback-speed"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpen('playback_speed');
                                }}
                            >
                                <ListItemIcon>
                                    <FastForwardIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t('content.events.video.controls.playback_speed')}
                                    sx={{ mx: 1 }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: palette.neutral[400],
                                        mx: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        lineHeight: 1,
                                    }}
                                >
                                    {playbackRateLabel}
                                </Typography>
                                <ChevronRightIcon fontSize="small" sx={{ color: palette.neutral[400] }} />
                            </MenuItem>
                        )}
                    </Menu>
                    <Menu
                        id="playback-speed-menu"
                        anchorEl={menuOpen === 'playback_speed' ? settingsButtonRef.current : null}
                        open={menuOpen === 'playback_speed'}
                        onClose={() => setMenuOpen(null)}
                        disablePortal={!!props.fullScreen}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        PaperProps={{
                            sx: {
                                backgroundColor: 'rgba(38, 38, 38, 0.8)',
                                color: palette.neutral[50],
                                minWidth: 200,
                                mt: -0.25,
                                '& .MuiListItemIcon-root': { color: palette.neutral[50], minWidth: 36 },
                                '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                            },
                        }}
                        MenuListProps={{ sx: { py: 0 } }}
                    >
                        <MenuItem
                            id="menu-playback-speed-back"
                            onClick={() => {
                                setMenuOpen('settings');
                            }}
                            sx={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                <ArrowBackIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t('content.events.video.controls.playback_speed')} />
                        </MenuItem>
                        {PLAYBACK_SPEED_OPTIONS.map((rate) => {
                            const isSelected = Math.abs((props.playbackRate ?? 1) - rate) < 0.01;
                            return (
                                <MenuItem
                                    key={rate}
                                    id={`menu-speed-${rate}`}
                                    onClick={() => {
                                        props.onPlaybackRateChange?.(rate);
                                        setMenuOpen(null);
                                    }}
                                >
                                    <ListItemText
                                        primary={`${rate} ×`}
                                        primaryTypographyProps={{
                                            sx: {
                                                color: isSelected ? palette.secondary : palette.neutral[50],
                                                fontWeight: isSelected ? 800 : 400,
                                            },
                                        }}
                                    />
                                </MenuItem>
                            );
                        })}
                    </Menu>
                    <Tooltip
                        title={t(`content.events.video.controls.${props.fullScreen ? 'exit' : 'open'}_full_screen`)}
                    >
                        <Box>
                            <IconButton
                                id="full-screen-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.fullScreen
                                        ? document.exitFullscreen()
                                        : props.targetRef.current?.requestFullscreen?.();
                                    props.setFullScreen?.((prev) => !prev);
                                }}
                                sx={{ color: palette.neutral[50], px: 0.25 }}
                            >
                                {props.fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default VideoControlsComponent;
